import { Dialog, Card, CardContent, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import '@styles/common.scss'
import { useDataProvider, useNotify } from 'react-admin'
import styles from './Poll.module.scss'
import Poll from './Poll'
import { LxLoadingSpinner } from '@src/components/loader/loadingSpinner'
import { cn } from '@src/utils/cn.ts'
import { PollB, PollF, PollModalF } from './Components/PollComponentType'
import modalStyles from '@components/modal/modal.module.scss'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import { POLL_STATUS } from '@src/utils/constants'
import { useModal } from '@src/logic/contexts/Modal/ModalContext'

interface PollModalProps {
  pollId: string | null
  poll: PollModalF | null
  resourceId: string
  pollScope: 'USER' | 'LEARNER'
  backToPath: string
}

export const mapServerPollToClient = (poll: PollB): PollF => ({
  id: poll.id,
  pollId: poll.poll_id,
  pollTemplateId: poll.poll_template_id,
  lastQuestion: poll.last_question,
  lastUpdate: poll.last_update,
  pollType: poll.poll_type,
  status: poll.status,
  name: poll.name,
  description: poll.description,
  duration: poll.duration,
  nQuestions: poll.nquestions,
  uiProperties: {
    hideProgress: poll.ui_properties?.hide_progress === 'true',
    showQuestionNumber: poll.ui_properties?.show_question_number === 'true',
    transitionEffect: poll.ui_properties?.transition_effect,
    ...poll.ui_properties,
  },
  extras: poll.extras ?? {},
  question: {
    id: poll.question.id,
    type: poll.question.type,
    optional: poll.question.optional === 'true',
    properties: poll.question.properties,
  },
})

export const PollModal: React.FC<PollModalProps> = ({ pollId, poll, resourceId, pollScope, backToPath }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const [pollData, setPollData] = useState<PollF | null>(null)
  const [pollStarted, setPollStarted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [skipped, setSkipped] = useState(false)
  const { hideModal } = useModal()

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (pollId) {
          const { data } = await dataProvider.getOne('Poll', {
            id: resourceId,
            pollId: pollId,
            scope: pollScope,
          })
          setPollData(mapServerPollToClient(data))
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        const errorMessage = error.message || 'Something went wrong'
        notify(errorMessage, { type: 'warning' })
      }
    }

    fetchData()
  }, [pollId, resourceId, pollScope])

  if (pollData?.status === POLL_STATUS.COMPLETED) {
    return null
  }

  if (skipped) {
    hideModal()
    return null
  }

  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <Dialog open role='dialog' aria-labelledby='create-user-dialog-title' maxWidth='md' fullWidth aria-label='User Info'>
          <LxLoadingSpinner />
        </Dialog>
      )
    }

    if (poll?.requirementLevel === 'obligatory' && pollData) {
      return <Poll pollData={pollData} pollScope={pollScope} resourceId={resourceId} pollId={pollId!} backToPath={backToPath} />
    }

    if (poll && Number(poll.lastQuestion) < 1 && !pollStarted && pollData) {
      return (
        <Dialog open role='dialog' aria-labelledby='create-user-dialog-title' maxWidth='md' fullWidth aria-label='User Info'>
          <div className={modalStyles.dialogHeader}>
            <h3>{pollData?.name}</h3>
          </div>
          <div className={modalStyles.dialogContent}>
            {pollData.description && (
              <div className={styles.pollDescription}>
                <p>{pollData.description}</p>
              </div>
            )}
            <div className={styles.pollDetails}>
              <Card className={styles.pollCard}>
                <CardContent>
                  <div className={styles.cardContent}>
                    <FormatListNumberedIcon className={styles.cardIcon} />
                    <Typography variant='h4' component='div' className={styles.cardNumber}>
                      {pollData.nQuestions}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Total Questions
                    </Typography>
                  </div>
                </CardContent>
              </Card>
              <Card className={styles.pollCard}>
                <CardContent>
                  <div className={styles.cardContent}>
                    <HourglassEmptyIcon className={styles.cardIcon} />
                    <Typography variant='h4' component='div' className={styles.cardNumber}>
                      {Math.round(parseInt(pollData.duration) / 60)}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Time Required
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={cn(styles.dialogActions)}>
            <LxSaveButton onSubmit={() => setSkipped(true)} label='Skip' classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' />
            <LxSaveButton onSubmit={() => setPollStarted(true)} label='Continue' classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' />
          </div>
        </Dialog>
      )
    }

    if ((poll && Number(poll.lastQuestion) > 0) || pollStarted) {
      return pollData && <Poll pollData={pollData} pollScope={pollScope} resourceId={resourceId} pollId={pollId!} backToPath={backToPath} />
    }
  }

  return renderDialogContent()
}
